import React, { useEffect, useMemo, useState } from 'react';
import { Lock, Loader2, AlertCircle, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

/**
 * CashPinSetupGate — first-login (and post-reset) PIN setup modal.
 *
 * Mounted globally inside AuthProvider so it can intercept any authenticated
 * staff user whose `cashApprovalPin.status` is PIN_PENDING or whose admin
 * has flagged `forceReset = true`. The gate is non-dismissable (no outside
 * click, no close button) — the staff must either complete setup or log out.
 * This mirrors how a real cash-handling workflow forces enrollment before
 * the staff can authorize any payment.
 *
 * Once setup succeeds, the gate patches the in-memory + localStorage user
 * object so subsequent kiosk cash flows can proceed without a re-login.
 */
const CashPinSetupGate = ({ user, onSetupComplete, onSkipLogout }) => {
  const needsSetup = useMemo(() => {
    if (!user) return false;
    const cap = user.cashApprovalPin;
    // No PIN object = this person may not hold a cash PIN (customers, and
    // since Task 437 every staff role other than Admin, Manager, Manifest).
    if (!cap || cap.eligible === false) return false;
    return cap.status !== 'PIN_ACTIVE' && cap.status !== 'DISABLED';
  }, [user]);

  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (needsSetup) {
      setPin('');
      setConfirmPin('');
      setShowPin(false);
      setShowConfirm(false);
      setError('');
      setSubmitting(false);
    }
  }, [needsSetup]);

  if (!needsSetup) return null;

  const pinOk = /^[0-9]{4,6}$/.test(pin);
  const matchOk = pin && pin === confirmPin;
  const forced = !!user.cashApprovalPin?.forceReset;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!pinOk) {
      setError('PIN must be 4-6 digits (numbers only).');
      return;
    }
    if (!matchOk) {
      setError('PIN and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/auth/cash-pin/setup', {
        pin,
        confirmPin,
      });
      onSetupComplete?.(data?.data || { status: 'PIN_ACTIVE' });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not save PIN. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* non-dismissable */ }}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-700" />
            <DialogTitle>
              {forced ? 'Reset Your Cash Approval PIN' : 'Set Up Your Cash Approval PIN'}
            </DialogTitle>
          </div>
          <DialogDescription>
            {forced
              ? 'An administrator has reset your PIN. Choose a new 4-6 digit PIN before approving any kiosk cash payments.'
              : 'Choose a 4-6 digit PIN that you will use to authorize kiosk cash payments. Keep it private — never share it with customers or other staff.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="new-cash-pin">New PIN (4-6 digits)</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="new-cash-pin"
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="new-password"
                autoFocus
                maxLength={6}
                disabled={submitting}
                value={pin}
                onChange={(e) =>
                  setPin(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="pl-9 pr-10 tracking-widest text-lg"
                placeholder="••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPin((v) => !v)}
                disabled={submitting}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
              >
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirm-cash-pin">Confirm PIN</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="confirm-cash-pin"
                type={showConfirm ? 'text' : 'password'}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="new-password"
                maxLength={6}
                disabled={submitting}
                value={confirmPin}
                onChange={(e) =>
                  setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="pl-9 pr-10 tracking-widest text-lg"
                placeholder="••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirm((v) => !v)}
                disabled={submitting}
                aria-label={showConfirm ? 'Hide PIN' : 'Show PIN'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <p className="text-xs text-muted-foreground">
              You will type this PIN at the kiosk to authorize cash payments.
              Forgot it later? Ask an admin to reset it for you.
            </p>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onSkipLogout}
              disabled={submitting}
            >
              Log out
            </Button>
            <Button type="submit" disabled={submitting || !pinOk || !matchOk}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save PIN'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CashPinSetupGate;
